<template>
  <StudioMultiSelect
    v-model="selectedUids"
    :searchable="true"
    :items="users"
    :item-text="itemText"
    item-value="uid"
    :label="label"
    :placeholder="selectionLabel"
    :loading="isLoading"
    :search-input.sync="search"
    :clearable="clearable"
    @change="toggleUser"
    @filterUser="filterUser"
    @ignoreUser="ignoreUser"
    @clearClick="$emit('clearInfo', label)"
  >
    <template v-slot:prepend-item />

    <template v-slot:selection="{index}">
      <span v-if="index === 0">
        {{ selectionLabel }}
      </span>
    </template>

    <template v-slot:item="{item}">
      <UserSelectItem
        :uid="item.uid"
        :name="item.name"
        :username="item.username"
        :channel="item.channel"
        :should-ignore="item.shouldIgnore"
        :profile-picture="item.profile_picture"
        @filter-user="filterUser"
        @ignore-user="ignoreUser"
      />
    </template>
  </StudioMultiSelect>
</template>

<script>
import StudioMultiSelect from '@/components/filter/models/StudioMultiSelect';
import UserSelectItem from '@/components/filter/UserSelectItem';
import { groupDuplicatedUsers } from '@/services/WarRoomService';
import { mapActions } from 'vuex';

export default {
  name: 'UserFilter',
  components: {
    StudioMultiSelect,
    UserSelectItem,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    itemId: {
      type: [String, Number],
      required: false,
      default: '',
    },
    clearable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const selectedUsers = groupDuplicatedUsers(this.value?.users || []);

    return {
      selectedUsers,
      requestStatus: '',
      label: 'Autores',
      search: '',
      users: [],
      selectedUids: selectedUsers.map(({ uid }) => uid),
    };
  },
  computed: {
    isLoading() {
      return this.requestStatus === 'loading';
    },
    selectionLabel() {
      const selectedUsers = this.groupedUsers.filter(user => !user.shouldIgnore).length;
      const ignoredUsers = this.groupedUsers.filter(user => user.shouldIgnore).length;
      const label = [];

      if (selectedUsers) {
        label.push(`${selectedUsers} filtrado${selectedUsers > 1 ? 's' : ''}`);
      }

      if (ignoredUsers) {
        label.push(`${ignoredUsers} negativado${ignoredUsers > 1 ? 's' : ''}`);
      }

      return label.join(', ') || 'Todos';
    },
    groupedUsers() {
      return groupDuplicatedUsers(this.selectedUsers);
    },
  },
  watch: {
    search(newText) {
      if (!newText) {
        this.users = this.groupedUsers;
        return;
      }

      this.find(newText);
    },
    value(newValue, oldValue) {
      if (newValue !== oldValue && newValue.users?.length) {
        return;
      }

      this.selectedUsers = [];
      this.users = [];
    },
  },
  methods: {
    ...mapActions('cards', [
      'getWarroomService',
    ]),
    itemText({ name, username }) {
      // allow to search by name and username
      return `${name} ${username}`;
    },
    async find(q) {
      this.requestStatus = 'loading';

      try {
        const users = await this.getWarroomService({
          itemId: this.itemId,
          servicePath: 'searchUser',
          serviceParams: { q },
        });

        // remove selected users before this search.
        // when you select the user at search, the user
        // persist in list to change filter type, if you want
        this.users = users.filter(({ uid }) => !this.selectedUids.includes(uid));

        this.requestStatus = 'success';
      } catch {
        this.requestStatus = 'error';
      }
    },
    convertUsersToFilter(users, forceIgnore) {
      const convertedUsers = [];

      users.forEach(({
        duplicatedUsers,
        shouldIgnore = false,
        ...user
      }) => {
        const newShouldIgnore = forceIgnore === undefined ? shouldIgnore : forceIgnore;

        convertedUsers.push({
          ...user,
          shouldIgnore: newShouldIgnore,
        });

        // instead groups users helps UX,
        // we need to split them before send to server
        if (duplicatedUsers) {
          convertedUsers.push(...this.convertUsersToFilter(duplicatedUsers, newShouldIgnore));
        }
      });

      return convertedUsers;
    },
    unique(users) {
      const uniqueUsers = [];

      users.forEach((user) => {
        if (!uniqueUsers.find((u) => u.uid === user.uid)) {
          uniqueUsers.push(user);
        }
      });

      return uniqueUsers;
    },
    toggleFilterType(uid, shouldFilter) {
      const parser = user => {
        if (user.uid !== uid) {
          return user;
        }

        user.shouldIgnore = !shouldFilter;

        return user;
      };

      // update state for autocomplete items
      this.users = this.users.map(parser);

      // if called in search,
      // set user with the filter type value
      if (!this.groupedUsers.find((user) => user.uid === uid)) {
        this.toggleUser(this.groupedUsers.map((user) => user.uid).concat(uid));
      }

      const newUsers = this.groupedUsers.map(parser);
      this.setUsers(this.convertUsersToFilter(newUsers));
    },
    filterUser({ uid }) {
      this.toggleFilterType(uid, true);
    },
    ignoreUser({ uid }) {
      this.toggleFilterType(uid, false);
    },
    toggleUser(userIds) {
      const filterSelected = ({ uid }) => userIds.includes(uid);
      const usersFromSearch = this.users.filter(filterSelected);

      // get users selected before,
      // concat with new selection, from the autocomplete,
      // and remove duplicates
      const users = this.unique([
        ...this.convertUsersToFilter(this.groupedUsers.filter(filterSelected)),
        ...this.convertUsersToFilter(usersFromSearch),
      ]);

      // update state for autocomplete items
      usersFromSearch.forEach(user => {
        user.shouldIgnore = user.shouldIgnore || false;
      });

      // if users is empty,
      // reset the filter type for autocomplete
      if (!users.length) {
        this.users.forEach((user) => {
          delete user.shouldIgnore;
        });
      }

      this.setUsers(users);
    },
    setUsers(users) {
      const value = users.map(({ uid, shouldIgnore }) => shouldIgnore ? `-${uid}` : uid);

      this.selectedUids = users.map(({ uid }) => uid);
      this.selectedUsers = users;

      if (!this.search) {
        this.users = this.groupedUsers;
      }

      this.$emit('input', users.length ? { users, value } : undefined);
    },
  },
};

</script>

<style scoped lang="scss">
  .v-select__selection.v-select__selection--comma {
    line-height: 120%;
  }
</style>
